import { useEffect, useMemo, useState } from 'react';
import { ReportPivot } from '../reports/ReportPivot';
import '../reports/reportGrid.css';

/**
 * c360 listeleri için Pivot köprüsü — sabit raporlardaki ReportPivot'u TanStack tablosuna bağlar.
 *
 * NEDEN AYRI BİR BİLEŞEN: ReportPivot düz `rows` (nesne dizisi) + `columns` ({key,label,num})
 * bekler; c360 ise TanStack `table` instance'ı ile çalışır ve satırları ham entity'dir
 * (kolon değeri accessorFn'den gelebilir). Bu bileşen ikisini birbirine çevirir.
 *
 * VERİ KAPSAMI: c360 listeleri sunucu tarafında SAYFALIDIR. Yalnız yüklü sayfa üzerinde pivot
 * yapmak yanıltıcı olurdu (kullanıcı "toplam" sanır, oysa 25 satırın toplamıdır). Bu yüzden
 * sayfa `loadAllRows` verirse (useC360List.fetchAllRows) pivot AÇILIRKEN mevcut filtrelere uyan
 * TÜM satırları çeker; vermezse yüklü satırlarla çalışır ve bunu üstte açıkça yazar.
 */

const STORAGE_PREFIX = 'c360Pivot.v1.';
// Pivot'ta anlamı olmayan teknik kolonlar.
const SKIP_COLUMNS = new Set(['actions', 'select', 'expander', 'rowNumber', 'index']);

function readStored(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// TanStack başlığı string olabilir (columnHelper.accessor(..., { header: 'Ünvan' })) ya da
// render fonksiyonu. Fonksiyonsa kolon kimliğine düşeriz — pivot etiketi boş kalmasın.
function headerLabel(column) {
  const h = column.columnDef?.header;
  if (typeof h === 'string' && h.trim()) return h;
  const meta = column.columnDef?.meta;
  if (meta && typeof meta.pivotLabel === 'string') return meta.pivotLabel;
  return column.id;
}

// Pivot yalnız ilkel değerlerle çalışır; nesne/dizi hücreler (nav property, JSX) elenir.
function primitive(v) {
  if (v === null || v === undefined) return null;
  const t = typeof v;
  if (t === 'number' || t === 'string' || t === 'boolean') return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return null;
}

export function C360Pivot({ table, pivotKey, loadAllRows }) {
  const [allRows, setAllRows] = useState(null);   // loadAllRows sonucu (ham entity dizisi)
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [config, setConfig] = useState(() => readStored(pivotKey));

  useEffect(() => {
    try { localStorage.setItem(STORAGE_PREFIX + pivotKey, JSON.stringify(config)); } catch { /* kota — best effort */ }
  }, [pivotKey, config]);

  // Pivot açıldığında tam veriyi bir kez çek.
  useEffect(() => {
    if (!loadAllRows) return undefined;
    let cancelled = false;
    setLoading(true); setLoadError(null);
    Promise.resolve()
      .then(loadAllRows)
      .then((r) => { if (!cancelled) setAllRows(Array.isArray(r) ? r : []); })
      .catch((e) => { if (!cancelled) setLoadError(e?.message || 'Veri alınamadı'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadAllRows]);

  const leafColumns = useMemo(
    () => table.getAllLeafColumns().filter((col) => col.getIsVisible() && !SKIP_COLUMNS.has(col.id)),
    [table],
  );

  // Yüklü satırlar: accessorFn'i çalıştırmak için row.getValue kullanılır (ham alan adı
  // her zaman kolon kimliğiyle aynı olmayabilir).
  // getRowModel() SAYFALANMIŞ modeldir — pivot 25 satırı "toplam" sanardı. Sayfalama
  // öncesi model alınır: istemci tarafında sayfalayan ekranlarda bu, çekilmiş verinin TAMAMIDIR.
  const loadedRows = useMemo(() => {
    const model = (table.getPrePaginationRowModel?.() ?? table.getRowModel()).rows
      .filter((r) => !r.getIsGrouped?.());
    return model.map((r) => {
      const o = {};
      leafColumns.forEach((col) => {
        let v = null;
        try { v = primitive(r.getValue(col.id)); } catch { v = null; }
        o[col.id] = v;
      });
      return o;
    });
  }, [table, leafColumns]);

  // Tam veri geldiyse onu kullan; ham entity olduğu için kolon kimliğiyle doğrudan okunur
  // (c360 kolon kimlikleri OData alan adlarının camelCase karşılığıdır).
  const fullRows = useMemo(() => {
    if (!allRows) return null;
    return allRows.map((raw) => {
      const o = {};
      leafColumns.forEach((col) => { o[col.id] = primitive(raw?.[col.id]); });
      return o;
    });
  }, [allRows, leafColumns]);

  const rows = fullRows ?? loadedRows;

  // Sayısallık veriden anlaşılır: kolonun dolu değerlerinin tamamı sayı ise metriktir.
  const columns = useMemo(() => {
    const sample = rows.slice(0, 200);
    return leafColumns.map((col) => {
      const vals = sample.map((r) => r[col.id]).filter((v) => v !== null && v !== undefined && v !== '');
      const num = vals.length > 0 && vals.every((v) => typeof v === 'number' || (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))));
      return { key: col.id, label: headerLabel(col), num };
    });
  }, [leafColumns, rows]);

  // Kapsam uyarısı yalnız GEREKTİĞİNDE: sunucu tarafında sayfalayan (manualPagination) bir
  // ekranda tam veri yoksa kullanıcı "toplam" sanmamalı. İstemci tarafında sayfalayan
  // ekranlarda yüklü veri zaten filtrenin tamamıdır — gereksiz uyarı güveni zedeler.
  const serverPaged = Boolean(table.options?.manualPagination);
  const scopeNote = fullRows
    ? `${rows.length} kayıt · filtreye uyan tüm veri`
    : serverPaged
      ? `${rows.length} kayıt · DİKKAT: yalnız yüklü sayfa özetleniyor, tüm veri değil`
      : `${rows.length} kayıt · yüklü verinin tamamı`;

  return (
    <div className="c360-pivot">
      <div className="c360-pivot-note">
        {loading ? 'Tüm veri çekiliyor…' : loadError ? `Tam veri alınamadı: ${loadError} · yüklü satırlarla gösteriliyor` : scopeNote}
      </div>
      <ReportPivot
        rows={rows}
        columns={columns}
        config={config}
        onConfigChange={setConfig}
      />
    </div>
  );
}
